import React, { Fragment, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '@solana/wallet-adapter-react';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { API_URL, OWNER_ADDRESS, OWNER_PERCENTAGE, SOLANA_RPC_URL } from '../constants';
import SubscriptionConfirmModal from '../components/SubscriptionConfirmModal';

interface UserProfile {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  bio?: string;
  role: 'creator' | 'consumer';
  monthlyPrice?: number;
  yearlyPrice?: number;
  paymentWalletAddress?: string;
  walletAddress?: string;
  subscribersCount?: number;
  isSubscribed?: boolean;
  posts?: Post[];
}

interface Post {
  id: string;
  title: string;
  content: string;
  mediaUrls: string[];
  mediaTypes: string[];
  isPublic: boolean;
  likesCount: number;
  commentsCount: number;
  createdAt: string;
}

interface Stats {
  subscribersCount: number;
  postsCount: number;
}

const ProfileView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { publicKey, sendTransaction } = useWallet();
  const { token } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState<Stats>({ subscribersCount: 0, postsCount: 0 });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubscription, setPendingSubscription] = useState<{
    type: 'monthly' | 'annual';
    amount: number;
  } | null>(null);

  useEffect(() => {
    if (userId) {
      loadProfile();
    }
  }, [userId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user);
        setStats(data.stats);
      } else {
        setError('Failed to load profile');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const showSubscriptionModal = (type: 'monthly' | 'annual', solAmount: number) => {
    console.log('showSubscriptionModal called with:', { type, solAmount, typeof: typeof solAmount });
    
    if (!publicKey || !profile?.paymentWalletAddress) {
      setError('Please connect your wallet first and ensure creator has set up payment wallet');
      return;
    }

    const amount = Number(solAmount);
    console.log('Setting pending subscription:', { type, amount });
    
    setPendingSubscription({ type, amount });
    setShowConfirmModal(true);
    setError('');
  };

  const handleConfirmSubscription = async () => {
    if (!pendingSubscription || !profile) return;
    
    setShowConfirmModal(false);
    await processSubscription(pendingSubscription.type, pendingSubscription.amount);
  };

  const processSubscription = async (type: 'monthly' | 'annual', solAmount: number) => {
    if (!publicKey || !profile?.paymentWalletAddress) {
      setError('Please connect your wallet first and ensure creator has set up payment wallet');
      return;
    }

    setSubscribing(true);
    setError('');
    setSuccess('');

    try {
      // Create Solana transaction - use the same endpoint as wallet context
      const connection = new Connection(SOLANA_RPC_URL);
      if (!publicKey) {
        throw new Error('Wallet not connected');
      }
      const totalAmount = solAmount * LAMPORTS_PER_SOL;
      const ownerAmount = totalAmount * OWNER_PERCENTAGE;
      const creatorAmount = totalAmount - ownerAmount;
      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: new PublicKey(profile.paymentWalletAddress),
          lamports: creatorAmount
        }),
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: new PublicKey(OWNER_ADDRESS),
          lamports: ownerAmount
        })
      );

      // Get latest blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Send transaction through wallet
      const signature = await sendTransaction(transaction, connection);

      // Wait for confirmation
      await connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight: (await connection.getLatestBlockhash()).lastValidBlockHeight
      }, 'confirmed');

      // Create subscription in backend
      console.log('Creating subscription with data:', {
        userId: profile.id,
        type,
        amount: creatorAmount / LAMPORTS_PER_SOL,
        transactionSignature: signature
      });

      const response = await fetch(`${API_URL}/api/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: profile.id,
          type,
          amount: creatorAmount / LAMPORTS_PER_SOL,
          transactionSignature: signature
        })
      });

      console.log('Backend response status:', response.status);

      if (response.ok) {
        setSuccess('Subscription created successfully!');
        setTimeout(() => {
          loadProfile(); // Refresh to update subscription status after delay
        }, 1000);
      } else {
        const errorData = await response.json();
        console.log('Backend error response:', errorData);
        setError(errorData.error || 'Failed to create subscription');
      }
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      if (error.code === 4001) {
        setError('Transaction was cancelled by user');
      } else {
        setError('Failed to process payment: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setSubscribing(false);
      setPendingSubscription(null);
    }
  };

  const calculateAmounts = (totalAmount: number | undefined) => {
    if (!totalAmount || isNaN(totalAmount)) {
      return {
        total: 0,
        creator: 0,
        platform: 0,
        creatorPercentage: 80,
        platformPercentage: 20
      };
    }
    
    const total = Number(totalAmount);
    const ownerAmount = total * OWNER_PERCENTAGE;
    const creatorAmount = total - ownerAmount;
    
    console.log('calculateAmounts:', { 
      totalAmount, 
      total, 
      ownerAmount, 
      creatorAmount, 
      OWNER_PERCENTAGE 
    });
    
    return {
      total,
      creator: creatorAmount,
      platform: ownerAmount,
      creatorPercentage: Math.round((1 - OWNER_PERCENTAGE) * 100),
      platformPercentage: Math.round(OWNER_PERCENTAGE * 100)
    };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <Fragment>
        <div className="container-fluid py-2 py-md-4">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body text-center">
                  <h2 className="fw-700 font-lg text-grey-900">Profile not found</h2>
                  <p className="text-grey-500 mt-2">The user you're looking for doesn't exist.</p>
                  <button
                    onClick={() => navigate(-1)}
                    className="btn bg-current text-white font-xsss fw-600 p-3 rounded-3 mt-3"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="container-fluid py-2 py-md-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            {/* Back Button */}
            <div className="mb-3">
              <button
                onClick={() => navigate(-1)}
                className="btn bg-greylight text-grey-900 font-xsss fw-600 p-3 rounded-3"
              >
                <i className="feather-arrow-left me-2"></i>
                Back
              </button>
            </div>

            {error && (
              <div className="alert alert-danger font-xss fw-500 mb-3">
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success font-xss fw-500 mb-3">
                {success}
              </div>
            )}

            {/* Profile Header */}
            <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3"
              style={{ background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', minHeight: '200px' }}>
              <div className="card-body p-4 d-flex align-items-end">
                <div className="d-flex align-items-end w-100">
                  <div className="me-3">
                    <img
                      src={profile.avatar || '/assets/images/user.png'}
                      alt="Profile"
                      className="rounded-circle border border-white"
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                    />
                  </div>
                  <div className="text-white flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h2 className="fw-700 font-lg mb-0 me-2">
                        {profile.username || profile.email?.split('@')[0] || 'User'}
                      </h2>
                      {profile.role === 'creator' && (
                        <i className="feather-star text-warning"></i>
                      )}
                    </div>
                    <p className="font-xsss mb-1 opacity-75">
                      {profile.email}
                    </p>
                    {profile.bio && (
                      <p className="font-xsss mb-2 opacity-90">
                        {profile.bio}
                      </p>
                    )}
                    <div className="d-flex align-items-center">
                      <span className="badge bg-white text-dark font-xssss me-2">
                        {profile.role?.toUpperCase()}
                      </span>
                      <span className="font-xssss opacity-75 me-3">
                        <i className="feather-users me-1"></i>
                        {stats.subscribersCount} subscribers
                      </span>
                      <span className="font-xssss opacity-75">
                        <i className="feather-file-text me-1"></i>
                        {stats.postsCount} posts
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Section */}
            {profile.role === 'creator' && !profile.isSubscribed && (profile.monthlyPrice || profile.yearlyPrice) && profile.paymentWalletAddress && (
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body p-0">
                  <h4 className="fw-700 font-xss text-grey-900 mb-3">
                    Subscribe to {profile.username}
                  </h4>
                  <div className="d-flex flex-wrap gap-3">
                    {profile.monthlyPrice && (
                      <button
                        onClick={() => showSubscriptionModal('monthly', profile.monthlyPrice!)}
                        disabled={subscribing || !publicKey}
                        className="btn bg-current text-white font-xsss fw-600 p-3 rounded-3 flex-grow-1"
                      >
                        {subscribing ? 'Processing...' : `${profile.monthlyPrice} SOL/month`}
                      </button>
                    )}
                    {profile.yearlyPrice && (
                      <button
                        onClick={() => showSubscriptionModal('annual', profile.yearlyPrice!)}
                        disabled={subscribing || !publicKey}
                        className="btn bg-lightblue text-current font-xsss fw-600 p-3 rounded-3 flex-grow-1"
                      >
                        {subscribing ? 'Processing...' : `${profile.yearlyPrice} SOL/year`}
                      </button>
                    )}
                  </div>
                  {!publicKey && (
                    <p className="font-xsss text-danger mt-2">
                      Please connect your Solana wallet to subscribe
                    </p>
                  )}
                </div>
              </div>
            )}

            {profile.isSubscribed && (
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body p-0">
                  <div className="bg-success-light p-3 rounded-3">
                    <div className="d-flex align-items-center">
                      <i className="feather-check-circle text-success me-2"></i>
                      <span className="text-success font-xsss fw-600">
                        You're subscribed to this creator
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Creator hasn't set up payment wallet */}
            {profile.role === 'creator' && !profile.isSubscribed && (profile.monthlyPrice || profile.yearlyPrice) && !profile.paymentWalletAddress && (
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body p-0">
                  <div className="bg-warning-light p-3 rounded-3">
                    <div className="d-flex align-items-center">
                      <i className="feather-alert-triangle text-warning me-2"></i>
                      <span className="text-warning font-xsss fw-600">
                        This creator hasn't set up their payment wallet yet
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Posts Section */}
            <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3">
              <div className="card-body p-4 border-bottom">
                <h4 className="fw-700 font-xss text-grey-900 mb-0">Posts</h4>
              </div>

              {profile.posts && profile.posts.length > 0 ? (
                <div className="card-body p-0">
                  {profile.posts.map((post, index) => (
                    <div key={post.id} className={`p-4 ${index < profile.posts!.length - 1 ? 'border-bottom' : ''}`}>
                      <div className="d-flex">
                        <img
                          src={profile.avatar || '/assets/images/user.png'}
                          alt="Profile"
                          className="rounded-circle me-3"
                          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2">
                            <h5 className="fw-600 font-xss text-grey-900 mb-0 me-2">
                              {post.title}
                            </h5>
                            {!post.isPublic && (
                              <i className="feather-lock font-xsss text-grey-500"></i>
                            )}
                          </div>

                          <p className="font-xsss text-grey-700 mb-3">{post.content}</p>

                          {post.mediaUrls && post.mediaUrls.length > 0 && (
                            <div className="row g-2 mb-3">
                              {post.mediaUrls.map((url, mediaIndex) => (
                                <div key={mediaIndex} className="col-6">
                                  {!post.isPublic && !profile.isSubscribed ? (
                                    <div className="bg-greylight rounded-3 d-flex align-items-center justify-content-center"
                                      style={{ height: '150px' }}>
                                      <div className="text-center">
                                        <i className="feather-lock font-lg text-grey-500 mb-2"></i>
                                        <p className="font-xsss text-grey-600 mb-0">Subscribe to view</p>
                                      </div>
                                    </div>
                                  ) : (
                                    <img
                                      src={url}
                                      alt={`${post.title} - ${mediaIndex + 1}`}
                                      className="w-100 rounded-3"
                                      style={{ height: '150px', objectFit: 'cover' }}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="d-flex align-items-center text-grey-500 font-xssss">
                            <span className="me-3">
                              <i className="feather-heart me-1"></i>
                              {post.likesCount}
                            </span>
                            <span className="me-3">
                              <i className="feather-message-circle me-1"></i>
                              {post.commentsCount}
                            </span>
                            <span>
                              <i className="feather-calendar me-1"></i>
                              {formatDate(post.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card-body text-center p-5">
                  <i className="feather-file-text font-xl text-grey-400 mb-3"></i>
                  <h5 className="fw-600 font-xss text-grey-900 mb-1">No posts yet</h5>
                  <p className="font-xsss text-grey-500 mb-0">
                    This creator hasn't shared any posts yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Confirmation Modal */}
      {pendingSubscription && profile && (
        <SubscriptionConfirmModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setPendingSubscription(null);
          }}
          onConfirm={handleConfirmSubscription}
          creatorName={profile.username || profile.email?.split('@')[0] || 'User'}
          subscriptionType={pendingSubscription.type}
          totalAmount={pendingSubscription.amount}
          creatorAmount={calculateAmounts(pendingSubscription.amount).creator}
          platformAmount={calculateAmounts(pendingSubscription.amount).platform}
          creatorPercentage={calculateAmounts(pendingSubscription.amount).creatorPercentage}
          platformPercentage={calculateAmounts(pendingSubscription.amount).platformPercentage}
          isProcessing={subscribing}
        />
      )}
    </Fragment>
  );
};

export default ProfileView;
